import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Drawer,
  Box,
  Tooltip,
  Select,
  ClickAwayListener,
} from "@material-ui/core";
import {
  Add,
  Delete,
  Close,
  CloudUpload,
  Description,
  Save,
  Visibility,
  VisibilityOff,
  InsertDriveFile,
  CheckCircle,
  Warning,
  ListAlt,
  Timeline,
} from "@material-ui/icons";
import Button from "components/CustomButtons/Button";
import ModalHeader from "components/ModalHeader";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardIcon from "components/Card/CardIcon";
import CardBody from "components/Card/CardBody";
import FeedbackButton from "components/CTSGuide/FeedbackButton";
import TextEditor from "components/TextEditor/TextEditor";
import Accordion from "components/Accordion/Accordion";
import TrackChangesIcon from "@material-ui/icons/TrackChanges";
import { useTranslation } from "react-i18next";
import { client } from "services/axiosClient";
import ctsGuidePortalStyle from "assets/jss/material-dashboard-pro-react/views/ctsGuidePortalStyle";
import useThemeColors from "hooks/useThemeColors";
import { ThreeDots } from "react-loader-spinner";
import BuildStatus from "./BuildStatus";
import CTSMiniNav from "components/CTSGuide/CTSMiniNav";

const useStyles = makeStyles(ctsGuidePortalStyle);

// Status options
const STATUS_OPTIONS = [
  { value: "not_requested", label: "not_requested" },
  { value: "requested", label: "requested" },
  { value: "partial", label: "partial" },
  { value: "received", label: "received" },
  { value: "documented", label: "documented" },
];

// Quality options
const QUALITY_OPTIONS = [
  { value: "high", label: "high" },
  { value: "medium", label: "medium" },
  { value: "low", label: "low" },
];

// Representativeness options
const REPRESENTATIVENESS_OPTIONS = [
  { value: "high", label: "high" },
  { value: "medium", label: "medium" },
  { value: "low", label: "low" },
];

// Editable Cell Component
function EditableCell({
  value,
  onChange,
  type = "text",
  options = [],
  placeholder = "-",
  multiline = false,
  renderBadge = null,
  className = "",
  centered = false,
}) {
  const classes = useStyles();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || "");
  const inputRef = useRef(null);

  useEffect(() => {
    setEditValue(value || "");
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (type === "text" || multiline) {
        inputRef.current.select();
      }
    }
  }, [isEditing, type, multiline]);

  const handleClick = (e) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleBlur = () => {
    setIsEditing(false);
    if (editValue !== value) {
      onChange(editValue);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !multiline) {
      handleBlur();
    }
    if (e.key === "Escape") {
      setEditValue(value || "");
      setIsEditing(false);
    }
  };

  const handleSelectChange = (e) => {
    const newValue = e.target.value;
    setEditValue(newValue);
    onChange(newValue);
    setIsEditing(false);
  };

  if (isEditing) {
    if (type === "select") {
      return (
        <ClickAwayListener onClickAway={handleBlur}>
          <Select
            ref={inputRef}
            value={editValue}
            onChange={handleSelectChange}
            className={classes.inlineSelect}
            autoFocus
            open
            onClose={() => setIsEditing(false)}
          >
            <MenuItem value="">
              <em>-</em>
            </MenuItem>
            {options.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </ClickAwayListener>
      );
    }

    if (type === "date") {
      return (
        <ClickAwayListener onClickAway={handleBlur}>
          <input
            ref={inputRef}
            type="date"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className={classes.inlineInput}
          />
        </ClickAwayListener>
      );
    }

    if (multiline) {
      return (
        <ClickAwayListener onClickAway={handleBlur}>
          <textarea
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className={classes.inlineTextarea}
            rows={3}
          />
        </ClickAwayListener>
      );
    }

    return (
      <ClickAwayListener onClickAway={handleBlur}>
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={classes.inlineInput}
        />
      </ClickAwayListener>
    );
  }

  // Display mode
  const displayValue = value || "";
  const isEmpty = !displayValue;

  if (renderBadge && displayValue) {
    return (
      <div
        className={`${classes.editableCell} ${centered ? classes.editableCellCentered : ""} ${className}`}
        onClick={handleClick}
      >
        {renderBadge(displayValue)}
      </div>
    );
  }

  return (
    <div
      className={`${classes.editableCell} ${isEmpty ? classes.editableCellEmpty : ""} ${multiline ? classes.multilineCell : ""} ${className}`}
      onClick={handleClick}
    >
      {isEmpty ? placeholder : displayValue}
    </div>
  );
}

EditableCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  type: PropTypes.oneOf(["text", "select", "date"]),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  ),
  placeholder: PropTypes.string,
  multiline: PropTypes.bool,
  renderBadge: PropTypes.func,
  className: PropTypes.string,
  centered: PropTypes.bool,
};

export default function DataTracking() {
  const classes = useStyles();
  const { t } = useTranslation();
  const { clientColor } = useThemeColors();
  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  const [inputs, setInputs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialog, setAddDialog] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedInput, setSelectedInput] = useState(null);
  const [newInputForm, setNewInputForm] = useState({
    meta_name: "",
    input_group: "OUTROS",
  });
  const [initialSpecContent, setInitialSpecContent] = useState("");
  const [orderBy, setOrderBy] = useState("meta_name");
  const [orderDirection, setOrderDirection] = useState("asc");
  const [showExampleSpec, setShowExampleSpec] = useState(false);
  const specContentRef = useRef("");
  // Key to force TextEditor remount when drawer opens (prevents stale content)
  const [editorKey, setEditorKey] = useState(0);
  // Local state for drawer fields (to avoid API calls on every change)
  const [drawerSampleDescription, setDrawerSampleDescription] = useState("");
  const [drawerFrequency, setDrawerFrequency] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const fileInputRef = useRef(null);
  const initialValuesRef = useRef({
    specContent: "",
    sampleDescription: "",
    frequency: "",
  });
  // AUTO-SAVE REMOVED - was causing data corruption between inputs

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Single optimized call for all page data
      const res = await client.get("/api/ctsguide/data-tracking-data/");
      setInputs(res.data.inputs || []);
    } catch (error) {
      // Error loading data
    } finally {
      setLoading(false);
    }
  };

  // Handle sorting
  const handleSort = (column) => {
    if (orderBy === column) {
      setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
    } else {
      setOrderBy(column);
      setOrderDirection("asc");
    }
  };

  // Sort inputs
  const sortedInputs = [...inputs].sort((a, b) => {
    let aVal = a[orderBy] || "";
    let bVal = b[orderBy] || "";

    // Handle special cases
    if (orderBy === "deadline") {
      aVal = aVal ? new Date(aVal).getTime() : 0;
      bVal = bVal ? new Date(bVal).getTime() : 0;
    } else if (typeof aVal === "string") {
      aVal = aVal.toLowerCase();
      bVal = (bVal || "").toLowerCase();
    }

    if (aVal < bVal) return orderDirection === "asc" ? -1 : 1;
    if (aVal > bVal) return orderDirection === "asc" ? 1 : -1;
    return 0;
  });

  // Update a single field of an input
  const updateInputField = useCallback(async (inputId, field, value) => {
    try {
      await client.put(`/api/ctsguide/inputs/${inputId}/`, { [field]: value });
      setInputs((prev) =>
        prev.map((inp) =>
          inp.id === inputId ? { ...inp, [field]: value } : inp,
        ),
      );
    } catch (error) {
      console.error("Error updating input:", error);
      loadData(); // Reload on error
    }
  }, []);

  // Add new input
  const handleAddInput = async () => {
    if (!newInputForm.meta_name) return;

    try {
      await client.post("/api/ctsguide/inputs/", newInputForm);
      setAddDialog(false);
      setNewInputForm({
        meta_name: "",
        input_group: "OUTROS",
      });
      loadData();
    } catch (error) {
      console.error("Error adding input:", error);
    }
  };

  // Delete input
  const handleDeleteInput = async (e, inputId) => {
    e.stopPropagation();
    if (!window.confirm(t("ctsguide.inputs.confirm_delete"))) return;

    try {
      await client.delete(`/api/ctsguide/inputs/${inputId}/`);
      loadData();
    } catch (error) {
      console.error("Error deleting input:", error);
    }
  };

  // Open drawer for specification
  const handleOpenDrawer = (e, input) => {
    e.stopPropagation();
    setSelectedInput(input);
    const existingSpec = input.specification?.specification_text;
    const content = existingSpec || t("ctsguide.inputs.specification_template");
    setInitialSpecContent(content);
    specContentRef.current = content;
    setShowExampleSpec(false);
    // Initialize local drawer state
    const sampleDesc = input.sample_description || "";
    const freq = input.frequency || "";
    setDrawerSampleDescription(sampleDesc);
    setDrawerFrequency(freq);
    setHasUnsavedChanges(false);
    // Store initial values for comparison
    initialValuesRef.current = {
      specContent: content,
      sampleDescription: sampleDesc,
      frequency: freq,
    };
    // Increment editor key to force TextEditor remount with fresh content
    setEditorKey((prev) => prev + 1);
    setDrawerOpen(true);
  };

  // Check if there are unsaved changes
  const checkUnsavedChanges = () => {
    const initial = initialValuesRef.current;
    const hasChanges =
      specContentRef.current !== initial.specContent ||
      drawerSampleDescription !== initial.sampleDescription ||
      drawerFrequency !== initial.frequency;
    setHasUnsavedChanges(hasChanges);
    return hasChanges;
  };

  // Handle TextEditor content change
  const handleSpecificationChange = (content) => {
    specContentRef.current = content;
    checkUnsavedChanges();
  };

  // Handle drawer close attempt
  const handleDrawerCloseAttempt = () => {
    if (checkUnsavedChanges()) {
      setConfirmCloseOpen(true);
    } else {
      closeDrawer();
    }
  };

  // Close drawer and cleanup
  const closeDrawer = () => {
    setDrawerOpen(false);
    setHasUnsavedChanges(false);
    setConfirmCloseOpen(false);
  };

  // Discard changes and close
  const handleDiscardChanges = () => {
    closeDrawer();
  };

  // Save and close
  const handleSaveAndClose = async () => {
    await handleSaveSpecification();
    setConfirmCloseOpen(false);
  };

  // Save specification and upload fields
  const handleSaveSpecification = async () => {
    if (!selectedInput || saving) return;

    setSaving(true);
    try {
      // Save specification
      await client.put(
        `/api/ctsguide/inputs/${selectedInput.id}/specification/`,
        { specification_text: specContentRef.current },
      );
      // Save upload fields (sample_description and frequency)
      await client.put(`/api/ctsguide/inputs/${selectedInput.id}/`, {
        sample_description: drawerSampleDescription,
        frequency: drawerFrequency,
      });
      // Update initial values
      initialValuesRef.current = {
        specContent: specContentRef.current,
        sampleDescription: drawerSampleDescription,
        frequency: drawerFrequency,
      };
      setHasUnsavedChanges(false);
      // IMPORTANT: Wait for loadData to complete BEFORE closing drawer
      // This prevents race conditions when opening another input quickly
      await loadData();
      setDrawerOpen(false);
    } catch (error) {
      console.error("Error saving specification:", error);
    } finally {
      setSaving(false);
    }
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedInput) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      await client.post(
        `/api/ctsguide/inputs/${selectedInput.id}/sample/`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      // Update local state only (no page refresh)
      setSelectedInput({
        ...selectedInput,
        sample_file_name: file.name,
        has_sample_file: true,
      });
    } catch (error) {
      console.error("Error uploading file:", error);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Handle file delete
  const handleFileDelete = async () => {
    if (!selectedInput) return;

    try {
      await client.delete(`/api/ctsguide/inputs/${selectedInput.id}/sample/`);

      // Update local state only (no page refresh)
      setSelectedInput({
        ...selectedInput,
        sample_file_name: null,
        has_sample_file: false,
      });
    } catch (error) {
      console.error("Error deleting file:", error);
    }
  };

  // Render status badge
  const renderStatusBadge = (status) => {
    const statusClasses = {
      not_requested: classes.statusNotRequested,
      requested: classes.statusRequested,
      partial: classes.statusPartial,
      received: classes.statusReceived,
      documented: classes.statusDocumented,
    };

    return (
      <span className={`${classes.statusBadge} ${statusClasses[status] || ""}`}>
        {t(`ctsguide.input_status.${status}`)}
      </span>
    );
  };

  // Render quality badge
  const renderQualityBadge = (quality) => {
    if (!quality) return null;

    const qualityClasses = {
      high: classes.qualityHigh,
      medium: classes.qualityMedium,
      low: classes.qualityLow,
    };

    return (
      <span className={`${classes.qualityBadge} ${qualityClasses[quality]}`}>
        {t(`ctsguide.data_quality.${quality}`)}
      </span>
    );
  };

  // Render relevance badge
  const renderRelevanceBadge = (value) => {
    if (!value) return null;

    const qualityClasses = {
      high: classes.qualityHigh,
      medium: classes.qualityMedium,
      low: classes.qualityLow,
    };

    return (
      <span className={`${classes.qualityBadge} ${qualityClasses[value]}`}>
        {t(`ctsguide.relevance.${value}`)}
      </span>
    );
  };

  // Get translated options for select
  const getStatusOptions = () =>
    STATUS_OPTIONS.map((opt) => ({
      value: opt.value,
      label: t(`ctsguide.input_status.${opt.label}`),
    }));

  const getQualityOptions = () =>
    QUALITY_OPTIONS.map((opt) => ({
      value: opt.value,
      label: t(`ctsguide.data_quality.${opt.label}`),
    }));

  const getRelevanceOptions = () =>
    REPRESENTATIVENESS_OPTIONS.map((opt) => ({
      value: opt.value,
      label: t(`ctsguide.relevance.${opt.label}`),
    }));

  return (
    <div className={classes.pageContainer}>
      <Box display="flex" justifyContent="center" mb={0.5}>
        <CTSMiniNav />
      </Box>
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="client" icon>
              <CardIcon color="client">
                <TrackChangesIcon />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>
                {t("ctsguide.nav.cts_tracker")}
              </h4>
              <div className={classes.headerTabsRight}>
                <button
                  type="button"
                  className={`${classes.headerTab} ${activeTab === 0 ? classes.headerTabActive : ""}`}
                  onClick={() => handleTabChange(null, 0)}
                >
                  <ListAlt className={classes.headerTabIcon} />
                  {t("ctsguide.tabs.data_inputs")}
                </button>
                <button
                  type="button"
                  className={`${classes.headerTab} ${activeTab === 1 ? classes.headerTabActive : ""}`}
                  onClick={() => handleTabChange(null, 1)}
                >
                  <Timeline className={classes.headerTabIcon} />
                  {t("ctsguide.tabs.build_status")}
                </button>
              </div>
            </CardHeader>
            <CardBody>
              {/* Tab Content */}
              {activeTab === 0 && (
                <>
                  {loading ? (
                    <div className={classes.loadingContainer}>
                      <ThreeDots
                        width="120px"
                        color={clientColor}
                        ariaLabel="loading"
                      />
                    </div>
                  ) : (
                    <>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={2}
                      >
                        <div>
                          <p
                            style={{
                              color: "#666",
                              fontSize: "0.9em",
                              margin: 0,
                            }}
                          >
                            {t("ctsguide.inputs.description")}
                          </p>
                        </div>
                        <Tooltip title={t("ctsguide.inputs.add")}>
                          <span>
                            <Button
                              size="sm"
                              justIcon
                              color="client"
                              onClick={() => setAddDialog(true)}
                            >
                              <Add />
                            </Button>
                          </span>
                        </Tooltip>
                      </Box>

                      <div className={classes.dataTrackingContainer}>
                        <Table className={classes.inputsTable}>
                          <TableHead className={classes.inputsTableHeader}>
                            <TableRow>
                              <TableCell
                                className={classes.inputsTableHeaderCell}
                              >
                                <Tooltip
                                  title={t(
                                    "ctsguide.inputs.tooltips.input_name",
                                  )}
                                >
                                  <TableSortLabel
                                    active={orderBy === "meta_name"}
                                    direction={
                                      orderBy === "meta_name"
                                        ? orderDirection
                                        : "asc"
                                    }
                                    onClick={() => handleSort("meta_name")}
                                  >
                                    {t("ctsguide.inputs.input_name")}
                                  </TableSortLabel>
                                </Tooltip>
                              </TableCell>
                              <TableCell
                                className={classes.inputsTableHeaderCell}
                              >
                                <Tooltip
                                  title={t(
                                    "ctsguide.inputs.tooltips.granularity",
                                  )}
                                >
                                  <TableSortLabel
                                    active={orderBy === "granularity"}
                                    direction={
                                      orderBy === "granularity"
                                        ? orderDirection
                                        : "asc"
                                    }
                                    onClick={() => handleSort("granularity")}
                                  >
                                    {t("ctsguide.inputs.granularity")}
                                  </TableSortLabel>
                                </Tooltip>
                              </TableCell>
                              <TableCell
                                className={classes.inputsTableHeaderCell}
                                style={{ minWidth: "180px" }}
                              >
                                <Tooltip
                                  title={t(
                                    "ctsguide.inputs.tooltips.allocation_criteria",
                                  )}
                                >
                                  <span>
                                    {t("ctsguide.inputs.allocation_criteria")}
                                  </span>
                                </Tooltip>
                              </TableCell>
                              <TableCell
                                className={classes.inputsTableHeaderCell}
                              >
                                <Tooltip
                                  title={t("ctsguide.inputs.tooltips.status")}
                                >
                                  <TableSortLabel
                                    active={orderBy === "status"}
                                    direction={
                                      orderBy === "status"
                                        ? orderDirection
                                        : "asc"
                                    }
                                    onClick={() => handleSort("status")}
                                  >
                                    {t("ctsguide.inputs.status")}
                                  </TableSortLabel>
                                </Tooltip>
                              </TableCell>
                              <TableCell
                                className={classes.inputsTableHeaderCell}
                                style={{ minWidth: "150px" }}
                              >
                                <Tooltip
                                  title={t(
                                    "ctsguide.inputs.tooltips.status_comment",
                                  )}
                                >
                                  <span>
                                    {t("ctsguide.inputs.status_comment")}
                                  </span>
                                </Tooltip>
                              </TableCell>
                              <TableCell
                                className={classes.inputsTableHeaderCell}
                              >
                                <Tooltip
                                  title={t(
                                    "ctsguide.inputs.tooltips.data_quality",
                                  )}
                                >
                                  <TableSortLabel
                                    active={orderBy === "data_quality"}
                                    direction={
                                      orderBy === "data_quality"
                                        ? orderDirection
                                        : "asc"
                                    }
                                    onClick={() => handleSort("data_quality")}
                                  >
                                    {t("ctsguide.inputs.data_quality")}
                                  </TableSortLabel>
                                </Tooltip>
                              </TableCell>
                              <TableCell
                                className={classes.inputsTableHeaderCell}
                              >
                                <Tooltip
                                  title={t(
                                    "ctsguide.inputs.tooltips.relevance",
                                  )}
                                >
                                  <TableSortLabel
                                    active={orderBy === "representativeness"}
                                    direction={
                                      orderBy === "representativeness"
                                        ? orderDirection
                                        : "asc"
                                    }
                                    onClick={() =>
                                      handleSort("representativeness")
                                    }
                                  >
                                    {t("ctsguide.inputs.relevance")}
                                  </TableSortLabel>
                                </Tooltip>
                              </TableCell>
                              <TableCell
                                className={classes.inputsTableHeaderCell}
                              >
                                <Tooltip
                                  title={t("ctsguide.inputs.tooltips.owner")}
                                >
                                  <TableSortLabel
                                    active={orderBy === "owner"}
                                    direction={
                                      orderBy === "owner"
                                        ? orderDirection
                                        : "asc"
                                    }
                                    onClick={() => handleSort("owner")}
                                  >
                                    {t("ctsguide.inputs.owner")}
                                  </TableSortLabel>
                                </Tooltip>
                              </TableCell>
                              <TableCell
                                className={classes.inputsTableHeaderCell}
                              >
                                <Tooltip
                                  title={t(
                                    "ctsguide.inputs.tooltips.source_system",
                                  )}
                                >
                                  <TableSortLabel
                                    active={orderBy === "source_system"}
                                    direction={
                                      orderBy === "source_system"
                                        ? orderDirection
                                        : "asc"
                                    }
                                    onClick={() => handleSort("source_system")}
                                  >
                                    {t("ctsguide.inputs.source_system")}
                                  </TableSortLabel>
                                </Tooltip>
                              </TableCell>
                              <TableCell
                                className={classes.inputsTableHeaderCell}
                              >
                                <Tooltip
                                  title={t("ctsguide.inputs.tooltips.deadline")}
                                >
                                  <TableSortLabel
                                    active={orderBy === "deadline"}
                                    direction={
                                      orderBy === "deadline"
                                        ? orderDirection
                                        : "asc"
                                    }
                                    onClick={() => handleSort("deadline")}
                                  >
                                    {t("ctsguide.inputs.deadline")}
                                  </TableSortLabel>
                                </Tooltip>
                              </TableCell>
                              <TableCell
                                className={classes.inputsTableHeaderCell}
                                style={{ width: "60px" }}
                              ></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sortedInputs.map((input) => (
                              <TableRow
                                key={input.id}
                                className={classes.inputsTableRow}
                              >
                                {/* Input Name - Text */}
                                <TableCell className={classes.inputsTableCell}>
                                  <EditableCell
                                    value={input.meta_name}
                                    onChange={(val) =>
                                      updateInputField(
                                        input.id,
                                        "meta_name",
                                        val,
                                      )
                                    }
                                    placeholder={t(
                                      "ctsguide.inputs.meta_name_placeholder",
                                    )}
                                  />
                                </TableCell>

                                {/* Granularity - Text */}
                                <TableCell className={classes.inputsTableCell}>
                                  <EditableCell
                                    value={input.granularity}
                                    onChange={(val) =>
                                      updateInputField(
                                        input.id,
                                        "granularity",
                                        val,
                                      )
                                    }
                                    placeholder="-"
                                  />
                                </TableCell>

                                {/* Allocation Criteria - Multiline */}
                                <TableCell className={classes.inputsTableCell}>
                                  <EditableCell
                                    value={input.allocation_criteria}
                                    onChange={(val) =>
                                      updateInputField(
                                        input.id,
                                        "allocation_criteria",
                                        val,
                                      )
                                    }
                                    placeholder="-"
                                    multiline
                                  />
                                </TableCell>

                                {/* Status - Select with Badge */}
                                <TableCell className={classes.inputsTableCell}>
                                  <EditableCell
                                    value={input.status}
                                    onChange={(val) =>
                                      updateInputField(input.id, "status", val)
                                    }
                                    type="select"
                                    options={getStatusOptions()}
                                    renderBadge={renderStatusBadge}
                                    centered
                                  />
                                </TableCell>

                                {/* Status Comment - Multiline */}
                                <TableCell className={classes.inputsTableCell}>
                                  <EditableCell
                                    value={input.status_comment}
                                    onChange={(val) =>
                                      updateInputField(
                                        input.id,
                                        "status_comment",
                                        val,
                                      )
                                    }
                                    placeholder="-"
                                    multiline
                                  />
                                </TableCell>

                                {/* Data Quality - Select with Badge */}
                                <TableCell className={classes.inputsTableCell}>
                                  <EditableCell
                                    value={input.data_quality}
                                    onChange={(val) =>
                                      updateInputField(
                                        input.id,
                                        "data_quality",
                                        val || null,
                                      )
                                    }
                                    type="select"
                                    options={getQualityOptions()}
                                    renderBadge={renderQualityBadge}
                                    centered
                                  />
                                </TableCell>

                                {/* Relevance - Select with Badge */}
                                <TableCell className={classes.inputsTableCell}>
                                  <EditableCell
                                    value={input.representativeness}
                                    onChange={(val) =>
                                      updateInputField(
                                        input.id,
                                        "representativeness",
                                        val || null,
                                      )
                                    }
                                    type="select"
                                    options={getRelevanceOptions()}
                                    renderBadge={renderRelevanceBadge}
                                    centered
                                  />
                                </TableCell>

                                {/* Owner - Text */}
                                <TableCell className={classes.inputsTableCell}>
                                  <EditableCell
                                    value={input.owner}
                                    onChange={(val) =>
                                      updateInputField(input.id, "owner", val)
                                    }
                                    placeholder="-"
                                  />
                                </TableCell>

                                {/* Source System - Text */}
                                <TableCell className={classes.inputsTableCell}>
                                  <EditableCell
                                    value={input.source_system}
                                    onChange={(val) =>
                                      updateInputField(
                                        input.id,
                                        "source_system",
                                        val,
                                      )
                                    }
                                    placeholder="-"
                                  />
                                </TableCell>

                                {/* Deadline - Date */}
                                <TableCell className={classes.inputsTableCell}>
                                  <EditableCell
                                    value={input.deadline}
                                    onChange={(val) =>
                                      updateInputField(
                                        input.id,
                                        "deadline",
                                        val || null,
                                      )
                                    }
                                    type="date"
                                    placeholder="-"
                                  />
                                </TableCell>

                                {/* Actions */}
                                <TableCell
                                  className={`${classes.inputsTableCell} ${classes.actionButtonsCell}`}
                                >
                                  <Tooltip
                                    title={t("ctsguide.inputs.specification")}
                                  >
                                    <IconButton
                                      size="small"
                                      className={classes.actionButton}
                                      onClick={(e) =>
                                        handleOpenDrawer(e, input)
                                      }
                                    >
                                      <Description fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={t("common.delete")}>
                                    <IconButton
                                      size="small"
                                      className={classes.actionButton}
                                      onClick={(e) =>
                                        handleDeleteInput(e, input.id)
                                      }
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>

                      {inputs.length === 0 && (
                        <Box textAlign="center" p={4} style={{ color: "#999" }}>
                          <p>{t("ctsguide.inputs.no_inputs")}</p>
                          <Tooltip title={t("ctsguide.inputs.add_first")}>
                            <span>
                              <Button
                                size="sm"
                                justIcon
                                color="client"
                                style={{ marginTop: "10px" }}
                                onClick={() => setAddDialog(true)}
                              >
                                <Add />
                              </Button>
                            </span>
                          </Tooltip>
                        </Box>
                      )}

                      {/* Add Input Dialog */}
                      <Dialog
                        open={addDialog}
                        onClose={() => setAddDialog(false)}
                        maxWidth="sm"
                        fullWidth
                      >
                        <ModalHeader
                          title={t("ctsguide.inputs.add")}
                          onClose={() => setAddDialog(false)}
                          closeLabel={t("common.close")}
                          clientColor={clientColor}
                        />
                        <DialogContent>
                          <TextField
                            fullWidth
                            autoFocus
                            label={t("ctsguide.inputs.meta_name")}
                            value={newInputForm.meta_name}
                            onChange={(e) =>
                              setNewInputForm({
                                ...newInputForm,
                                meta_name: e.target.value,
                              })
                            }
                            margin="normal"
                            variant="outlined"
                            placeholder={t(
                              "ctsguide.inputs.meta_name_placeholder",
                            )}
                          />
                        </DialogContent>
                        <DialogActions style={{ padding: "16px 24px" }}>
                          <Tooltip title={t("common.cancel")}>
                            <span>
                              <Button
                                size="sm"
                                justIcon
                                color="system_gray"
                                onClick={() => setAddDialog(false)}
                              >
                                <Close />
                              </Button>
                            </span>
                          </Tooltip>
                          <Tooltip title={t("common.save")}>
                            <span>
                              <Button
                                size="sm"
                                justIcon
                                color="client"
                                onClick={handleAddInput}
                                disabled={!newInputForm.meta_name}
                              >
                                <Save />
                              </Button>
                            </span>
                          </Tooltip>
                        </DialogActions>
                      </Dialog>

                      {/* Specification Drawer */}
                      <Drawer
                        anchor="right"
                        open={drawerOpen}
                        onClose={handleDrawerCloseAttempt}
                        disableEscapeKeyDown={hasUnsavedChanges}
                        BackdropProps={{
                          style: { backgroundColor: "#000", opacity: "0.1" },
                          onClick: (e) => {
                            e.stopPropagation();
                            handleDrawerCloseAttempt();
                          },
                        }}
                        PaperProps={{
                          style: {
                            borderRadius: "10px",
                            margin: "1vh 1vh 1vh 0",
                            height: "98vh",
                            width: "720px",
                            maxWidth: "90vw",
                            overflowX: "hidden",
                          },
                        }}
                      >
                        <div className={classes.drawer}>
                          <ModalHeader
                            title={t("ctsguide.inputs.specification")}
                            subtitleParts={[
                              selectedInput?.meta_name ? (
                                <strong key="meta">
                                  {selectedInput.meta_name}
                                </strong>
                              ) : null,
                              drawerFrequency
                                ? `${t("ctsguide.inputs.frequency")}: ${drawerFrequency}`
                                : null,
                            ].filter(Boolean)}
                            pills={[
                              selectedInput?.status
                                ? {
                                    key: "status",
                                    label: t(
                                      `ctsguide.input_status.${selectedInput.status}`,
                                    ),
                                  }
                                : null,
                              hasUnsavedChanges
                                ? {
                                    key: "unsaved",
                                    label: t("ctsguide.inputs.unsaved_changes"),
                                    tone: "warning",
                                    dot: true,
                                  }
                                : null,
                            ].filter(Boolean)}
                            rightContent={
                              <Tooltip title={t("common.save")}>
                                <span>
                                  <Button
                                    size="sm"
                                    justIcon
                                    color="client"
                                    onClick={handleSaveSpecification}
                                    disabled={saving}
                                  >
                                    {saving ? (
                                      <ThreeDots
                                        height="16"
                                        width="16"
                                        color="#fff"
                                      />
                                    ) : (
                                      <Save />
                                    )}
                                  </Button>
                                </span>
                              </Tooltip>
                            }
                            onClose={handleDrawerCloseAttempt}
                            closeLabel={t("common.close")}
                            clientColor={clientColor}
                            variant="drawer"
                          />

                          <div
                            style={{
                              flex: 1,
                              overflowY: "auto",
                              overflowX: "hidden",
                            }}
                          >
                            <Accordion
                              collapses={[
                                {
                                  title: t("ctsguide.inputs.upload_file"),
                                  content: (
                                    <div>
                                      <p className={classes.drawerInstructions}>
                                        {t(
                                          "ctsguide.inputs.upload_instructions",
                                        )}
                                      </p>

                                      <div className={classes.drawerWarningBox}>
                                        <Warning />
                                        <p>
                                          {t("ctsguide.inputs.upload_warning")}
                                        </p>
                                      </div>

                                      {/* Upload area first */}
                                      <div
                                        style={{
                                          display: "flex",
                                          justifyContent: "center",
                                          marginBottom: "20px",
                                        }}
                                      >
                                        <input
                                          type="file"
                                          ref={fileInputRef}
                                          onChange={handleFileUpload}
                                          style={{ display: "none" }}
                                        />
                                        {selectedInput?.has_sample_file ? (
                                          <div
                                            style={{
                                              width: "100%",
                                              maxWidth: "400px",
                                              padding: "16px",
                                              borderRadius: "8px",
                                              backgroundColor: "#f8fdf8",
                                              border: "1px solid #c8e6c9",
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "12px",
                                            }}
                                          >
                                            <div
                                              style={{
                                                backgroundColor: "#e8f5e9",
                                                borderRadius: "50%",
                                                padding: "10px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                              }}
                                            >
                                              <InsertDriveFile
                                                style={{
                                                  fontSize: "1.5em",
                                                  color: "#4caf50",
                                                }}
                                              />
                                            </div>
                                            <div
                                              style={{ flex: 1, minWidth: 0 }}
                                            >
                                              <div
                                                style={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: "6px",
                                                  marginBottom: "2px",
                                                }}
                                              >
                                                <CheckCircle
                                                  style={{
                                                    fontSize: "14px",
                                                    color: "#4caf50",
                                                  }}
                                                />
                                                <span
                                                  style={{
                                                    fontSize: "0.75em",
                                                    color: "#4caf50",
                                                    fontWeight: 500,
                                                  }}
                                                >
                                                  {t(
                                                    "ctsguide.inputs.file_uploaded",
                                                  )}
                                                </span>
                                              </div>
                                              <span
                                                style={{
                                                  fontSize: "0.85em",
                                                  color: "#333",
                                                  wordBreak: "break-all",
                                                  display: "block",
                                                }}
                                              >
                                                {
                                                  selectedInput?.sample_file_name
                                                }
                                              </span>
                                            </div>
                                            <div
                                              style={{
                                                display: "flex",
                                                gap: "4px",
                                              }}
                                            >
                                              <Tooltip
                                                title={t("common.change")}
                                              >
                                                <IconButton
                                                  size="small"
                                                  onClick={() =>
                                                    fileInputRef.current?.click()
                                                  }
                                                  style={{ color: "#666" }}
                                                >
                                                  <CloudUpload fontSize="small" />
                                                </IconButton>
                                              </Tooltip>
                                              <Tooltip
                                                title={t("common.delete")}
                                              >
                                                <IconButton
                                                  size="small"
                                                  onClick={handleFileDelete}
                                                  style={{ color: "#999" }}
                                                >
                                                  <Delete fontSize="small" />
                                                </IconButton>
                                              </Tooltip>
                                            </div>
                                          </div>
                                        ) : (
                                          <div
                                            className={classes.uploadArea}
                                            style={{
                                              width: "100%",
                                              maxWidth: "400px",
                                              cursor: uploading
                                                ? "wait"
                                                : "pointer",
                                            }}
                                            onClick={() =>
                                              !uploading &&
                                              fileInputRef.current?.click()
                                            }
                                          >
                                            {uploading ? (
                                              <ThreeDots
                                                height="40"
                                                width="40"
                                                color="#999"
                                              />
                                            ) : (
                                              <>
                                                <CloudUpload
                                                  style={{
                                                    fontSize: "2.5em",
                                                    color: "#999",
                                                  }}
                                                />
                                                <p>
                                                  {t(
                                                    "ctsguide.inputs.upload_placeholder",
                                                  )}
                                                </p>
                                              </>
                                            )}
                                          </div>
                                        )}
                                      </div>

                                      {/* Description and frequency fields */}
                                      <div
                                        style={{
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: "16px",
                                        }}
                                      >
                                        <TextField
                                          label={t(
                                            "ctsguide.inputs.sample_description",
                                          )}
                                          placeholder={t(
                                            "ctsguide.inputs.sample_description_placeholder",
                                          )}
                                          value={drawerSampleDescription}
                                          onChange={(e) => {
                                            setDrawerSampleDescription(
                                              e.target.value,
                                            );
                                            setTimeout(checkUnsavedChanges, 0);
                                          }}
                                          variant="outlined"
                                          size="small"
                                          fullWidth
                                        />

                                        <TextField
                                          select
                                          label={t("ctsguide.inputs.frequency")}
                                          value={drawerFrequency}
                                          onChange={(e) => {
                                            setDrawerFrequency(e.target.value);
                                            setTimeout(checkUnsavedChanges, 0);
                                          }}
                                          variant="outlined"
                                          size="small"
                                          fullWidth
                                        >
                                          <MenuItem value="">
                                            {t(
                                              "ctsguide.inputs.frequency_placeholder",
                                            )}
                                          </MenuItem>
                                          <MenuItem value="monthly">
                                            {t("ctsguide.frequency.monthly")}
                                          </MenuItem>
                                          <MenuItem value="quarterly">
                                            {t("ctsguide.frequency.quarterly")}
                                          </MenuItem>
                                          <MenuItem value="semiannual">
                                            {t("ctsguide.frequency.semiannual")}
                                          </MenuItem>
                                          <MenuItem value="annual">
                                            {t("ctsguide.frequency.annual")}
                                          </MenuItem>
                                        </TextField>
                                      </div>
                                    </div>
                                  ),
                                },
                                {
                                  title: t("ctsguide.inputs.specification"),
                                  content: (
                                    <div>
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "8px",
                                          marginBottom: "12px",
                                        }}
                                      >
                                        <span
                                          className={classes.exampleToggle}
                                          onClick={() =>
                                            setShowExampleSpec(!showExampleSpec)
                                          }
                                        >
                                          {showExampleSpec ? (
                                            <VisibilityOff fontSize="small" />
                                          ) : (
                                            <Visibility fontSize="small" />
                                          )}
                                          <span
                                            className={
                                              classes.exampleToggleText
                                            }
                                          >
                                            {showExampleSpec
                                              ? t(
                                                  "ctsguide.inputs.hide_example",
                                                )
                                              : t(
                                                  "ctsguide.inputs.show_example",
                                                )}
                                          </span>
                                        </span>
                                      </div>

                                      <p className={classes.drawerInstructions}>
                                        {t(
                                          "ctsguide.inputs.specification_instructions",
                                        )}
                                      </p>

                                      {/* Exemplo expandível */}
                                      {showExampleSpec && (
                                        <div style={{ marginBottom: "16px" }}>
                                          <p
                                            className={
                                              classes.drawerExampleLabel
                                            }
                                          >
                                            {t(
                                              "ctsguide.inputs.example_specification",
                                            )}
                                          </p>
                                          <TextEditor
                                            initialContent={t(
                                              "ctsguide.inputs.specification_example",
                                            )}
                                            readOnly={true}
                                            setContent={() => {}}
                                          />
                                        </div>
                                      )}

                                      {/* Editor principal */}
                                      <TextEditor
                                        key={`${selectedInput?.id}_${editorKey}`}
                                        initialContent={initialSpecContent}
                                        readOnly={false}
                                        setContent={handleSpecificationChange}
                                      />
                                    </div>
                                  ),
                                },
                              ]}
                              customRootClass={classes.accordionRoot}
                              customTitleClass={classes.accordionTitle}
                              customExpandIcon={classes.expandIcon}
                              active={0}
                            />
                          </div>
                        </div>
                      </Drawer>

                      {/* Confirmation Dialog for unsaved changes */}
                      <Dialog
                        open={confirmCloseOpen}
                        onClose={() => setConfirmCloseOpen(false)}
                      >
                        <ModalHeader
                          title={t("ctsguide.inputs.unsaved_changes_title")}
                          pills={[
                            {
                              key: "warning",
                              label: t("common.warning"),
                              tone: "warning",
                              dot: true,
                            },
                          ]}
                          onClose={() => setConfirmCloseOpen(false)}
                          closeLabel={t("common.close")}
                          clientColor={clientColor}
                        />
                        <DialogContent>
                          <p>{t("ctsguide.inputs.unsaved_changes_message")}</p>
                        </DialogContent>
                        <DialogActions>
                          <Button
                            size="sm"
                            color="system_gray"
                            onClick={handleDiscardChanges}
                          >
                            {t("ctsguide.inputs.discard_changes")}
                          </Button>
                          <Button
                            size="sm"
                            color="client"
                            onClick={handleSaveAndClose}
                          >
                            {t("common.save")}
                          </Button>
                        </DialogActions>
                      </Dialog>
                    </>
                  )}
                </>
              )}

              {/* Build Status Tab */}
              {activeTab === 1 && <BuildStatus />}
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>

      {/* Floating Feedback Button */}
      <FeedbackButton pageOrigin="ctsTracker" />
    </div>
  );
}
